import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandLogo from "@/components/BrandLogo";
import { useBranding } from "@/context/BrandingContext";

export default function Login() {
  const { login } = useAuth();
  const { branding, refresh: refreshBranding, applyNeutral, exitNeutral } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { applyNeutral(); return () => exitNeutral(); }, [applyNeutral, exitNeutral]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      exitNeutral();
      await refreshBranding();
      navigate(u?.role === "superadmin" ? "/master" : "/tableau-de-bord");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-dd-night">
      {/* Panneau gauche marque */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-14 bg-dd-night text-white">
        <div className="self-start">
          <BrandLogo className="h-28" negative stacked />
        </div>
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
            Constats d'état<br />des lieux
          </h1>
          <p className="mt-6 text-lg text-dd-light max-w-md">
            Remise et restitution de la chose louée. Saisie sur tablette, sur le terrain,
            en toute simplicité.
          </p>
        </div>
        <p className="text-sm text-dd-light/80" data-testid="login-agence">
          {[branding.agence?.nom, branding.agence?.adresse].filter(Boolean).join(" · ") || branding.nom_application}
        </p>
      </div>

      {/* Formulaire */}
      <div className="flex-1 flex items-center justify-center bg-white p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 lg:hidden"><BrandLogo className="h-14" /></div>
          <h2 className="text-2xl font-bold text-dd-blue">Connexion</h2>
          <p className="text-sm text-dd-warm mt-1 mb-8">Accédez à votre espace gérance.</p>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-dd-night">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email}
                onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11" autoComplete="username" />
            </div>
            <div>
              <Label htmlFor="password" className="text-dd-night">Mot de passe</Label>
              <Input id="password" type="password" data-testid="login-password" value={password}
                onChange={(e) => setPassword(e.target.value)} required className="mt-1 h-11" autoComplete="current-password" />
            </div>
          </div>

          {error && <div className="mt-4 text-sm text-destructive" data-testid="login-error">{error}</div>}

          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="mt-6 w-full h-11 bg-dd-blue hover:bg-dd-night text-white text-base">
            {loading ? "Connexion…" : "Se connecter"}
          </Button>

          <Link to="/forgot-password" className="mt-4 block text-sm text-dd-blue hover:underline" data-testid="forgot-link">
            Mot de passe oublié ?
          </Link>
          <p className="mt-6 pt-5 border-t border-dd-light text-sm text-dd-warm">
            Nouvelle gérance ? <Link to="/inscription" className="text-dd-blue hover:underline font-medium" data-testid="register-link">Créer un compte</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
